import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { IoCloseOutline } from 'react-icons/io5';
import { ErrorBoundary } from 'react-error-boundary';
import { useDarkMode } from '../../context/DarkModeContext';
import LoadingSpinner from './LoadingSpinner';
import SearchBar from './SearchBar';
import TagsDropdown from './TagsDropdown';
import FilterPanels from './FilterPanels';
import { FilterItem, FilterState, TypeVisibility, EnhancedFilterModalProps } from './types';
import { extractTags } from '../../utils/tagUtils';

/**
 * PersistentFilterModal extends EnhancedFilterModal functionality
 * but maintains selected items state between modal opens/closes.
 * This is specifically designed for use in ContentSelection.tsx
 */
const PersistentFilterModal: React.FC<EnhancedFilterModalProps> = ({
  isOpen,
  onClose,
  title,
  initialNotes = [],
  initialCards = [],
  onFilterChange,
  onSubmit,
  submitButtonText = 'Get Suggestions',
  initialSelectedNotes = [],
  initialSelectedCards = [],
  typeVisibility = { notes: true, careerCards: true, reviews: false },
  children
}) => {
  const { darkMode } = useDarkMode();
  const [isLoading, setIsLoading] = useState(true);
  const [filterState, setFilterState] = useState<FilterState>(() => ({
    availableItems: {
      notes: initialNotes,
      cards: initialCards,
    },
    selectedItems: {
      notes: [],
      cards: [],
    },
    searchQuery: '',
    selectedTags: [],
    typeVisibility: typeVisibility,
  }));

  // Store previous open state to detect when modal is freshly opened
  const wasOpen = useRef(false);
  
  // Update filter state when modal opens for the first time
  useEffect(() => {
    if (isOpen && !wasOpen.current) {
      // Modal is opening
      setFilterState(prev => {
        // On first open, use the initial selected items
        const selectedNotes = prev.selectedItems.notes.length > 0 ? prev.selectedItems.notes : initialSelectedNotes;
        const selectedCards = prev.selectedItems.cards.length > 0 ? prev.selectedItems.cards : initialSelectedCards;
        
        return {
          ...prev,
          availableItems: {
            notes: initialNotes.filter(note => 
              !selectedNotes.some(selected => selected.id === note.id)
            ),
            cards: initialCards.filter(card => 
              !selectedCards.some(selected => selected.id === card.id)
            ),
          },
          selectedItems: {
            notes: selectedNotes,
            cards: selectedCards,
          },
          searchQuery: '',
          selectedTags: [],
          typeVisibility: typeVisibility,
        };
      });
    }
    wasOpen.current = isOpen;
  }, [isOpen]); // Only depend on isOpen to avoid infinite loops

  // Extract all unique tags with their types using the utility
  const allTags = useMemo(() => {
    const allItems = [...initialNotes, ...initialCards];
    return extractTags(allItems, {
      notes: {
        customTags: true,
        dateTags: true,
      },
      cards: {
        customTags: true,
        dateTags: true,
      },
    });
  }, [initialNotes, initialCards]);

  // Generate unique keys for items
  const getItemKey = useCallback((item: FilterItem) => {
    return `${item.type}-${item.id}`;
  }, []);

  // Load saved state from localStorage - removed as we want to start fresh each time
  useEffect(() => {
    if (isOpen) {
      setIsLoading(true);
      // Simulate loading delay for smoother UX
      setTimeout(() => setIsLoading(false), 300);
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, onClose]);

  const handleSearchChange = (query: string) => {
    setFilterState(prev => ({ ...prev, searchQuery: query }));
  };

  const handleTagsChange = (tags: string[]) => {
    setFilterState(prev => ({ ...prev, selectedTags: tags }));
  };

  const handleItemSelect = (items: FilterItem[]) => {
    console.log('Selecting items:', items);
    setFilterState(prev => {
      const newState = { ...prev };
      items.forEach(item => {
        const type = item.type === 'note' ? 'notes' : 'cards';
        newState.availableItems[type] = newState.availableItems[type].filter(i => i.id !== item.id);
        if (!newState.selectedItems[type].find(i => i.id === item.id)) {
          newState.selectedItems[type] = [...newState.selectedItems[type], item];
        }
      });
      console.log('New filter state after selection:', newState);
      return newState;
    });
  };

  const handleItemDeselect = (items: FilterItem[]) => {
    console.log('Deselecting items:', items);
    setFilterState(prev => {
      const newState = { ...prev };
      items.forEach(item => {
        const type = item.type === 'note' ? 'notes' : 'cards';
        newState.selectedItems[type] = newState.selectedItems[type].filter(i => i.id !== item.id);
        if (!newState.availableItems[type].find(i => i.id === item.id)) {
          newState.availableItems[type] = [...newState.availableItems[type], item];
        }
      });
      console.log('New filter state after deselection:', newState);
      return newState;
    });
  };

  const handleMoveAllRight = () => {
    setFilterState(prev => {
      const filteredNotes = prev.availableItems.notes.filter(item => {
        const matchesSearch = !prev.searchQuery || 
          item.title.toLowerCase().includes(prev.searchQuery.toLowerCase());
        const itemTags = [...(item.custom_tags || []), ...(item.date_tags || [])];
        const matchesTags = prev.selectedTags.length === 0 ||
          prev.selectedTags.every(tag => itemTags.includes(tag));
        return matchesSearch && matchesTags;
      });

      const filteredCards = prev.availableItems.cards.filter(item => {
        const matchesSearch = !prev.searchQuery || 
          item.title.toLowerCase().includes(prev.searchQuery.toLowerCase());
        const itemTags = [
          ...(item.custom_tags || item.card_data?.custom_tags || []),
          ...(item.date_tags || item.card_data?.date_tags || [])
        ];
        const matchesTags = prev.selectedTags.length === 0 ||
          prev.selectedTags.every(tag => itemTags.includes(tag));
        return matchesSearch && matchesTags;
      });

      return {
        ...prev,
        availableItems: {
          notes: prev.availableItems.notes.filter(item => !filteredNotes.includes(item)),
          cards: prev.availableItems.cards.filter(item => !filteredCards.includes(item)),
        },
        selectedItems: {
          notes: [...prev.selectedItems.notes, ...filteredNotes],
          cards: [...prev.selectedItems.cards, ...filteredCards],
        },
      };
    });
  };

  const handleMoveAllLeft = () => {
    setFilterState(prev => {
      const filteredNotes = prev.selectedItems.notes.filter(item => {
        const matchesSearch = !prev.searchQuery || 
          item.title.toLowerCase().includes(prev.searchQuery.toLowerCase());
        const itemTags = [...(item.custom_tags || []), ...(item.date_tags || [])];
        const matchesTags = prev.selectedTags.length === 0 ||
          prev.selectedTags.every(tag => itemTags.includes(tag));
        return matchesSearch && matchesTags;
      });

      const filteredCards = prev.selectedItems.cards.filter(item => {
        const matchesSearch = !prev.searchQuery || 
          item.title.toLowerCase().includes(prev.searchQuery.toLowerCase());
        const itemTags = [
          ...(item.custom_tags || item.card_data?.custom_tags || []),
          ...(item.date_tags || item.card_data?.date_tags || [])
        ];
        const matchesTags = prev.selectedTags.length === 0 ||
          prev.selectedTags.every(tag => itemTags.includes(tag));
        return matchesSearch && matchesTags;
      });

      return {
        ...prev,
        selectedItems: {
          notes: prev.selectedItems.notes.filter(item => !filteredNotes.includes(item)),
          cards: prev.selectedItems.cards.filter(item => !filteredCards.includes(item)),
        },
        availableItems: {
          notes: [...prev.availableItems.notes, ...filteredNotes],
          cards: [...prev.availableItems.cards, ...filteredCards],
        },
      };
    });
  };

  const handleTypeVisibilityChange = (type: keyof TypeVisibility, visible: boolean) => {
    setFilterState(prev => ({
      ...prev,
      typeVisibility: {
        ...prev.typeVisibility,
        [type]: visible
      }
    }));
  };

  // Filter items based on type visibility
  const filteredAvailableItems = useMemo(() => {
    return {
      notes: filterState.availableItems.notes.filter(item => {
        const matchesSearch = !filterState.searchQuery || 
          item.title.toLowerCase().includes(filterState.searchQuery.toLowerCase());
        const itemTags = [...(item.custom_tags || []), ...(item.date_tags || [])];
        const matchesTags = filterState.selectedTags.length === 0 ||
          filterState.selectedTags.every(tag => itemTags.includes(tag));
        return matchesSearch && matchesTags && filterState.typeVisibility.notes;
      }),
      cards: filterState.availableItems.cards.filter(item => {
        const matchesSearch = !filterState.searchQuery || 
          item.title.toLowerCase().includes(filterState.searchQuery.toLowerCase());
        const itemTags = [
          ...(item.custom_tags || item.card_data?.custom_tags || []),
          ...(item.date_tags || item.card_data?.date_tags || [])
        ];
        const matchesTags = filterState.selectedTags.length === 0 ||
          filterState.selectedTags.every(tag => itemTags.includes(tag));
        return matchesSearch && matchesTags && filterState.typeVisibility.careerCards;
      })
    };
  }, [filterState]);

  const filteredSelectedItems = useMemo(() => {
    return {
      notes: filterState.selectedItems.notes.filter(item => {
        const matchesSearch = !filterState.searchQuery || 
          item.title.toLowerCase().includes(filterState.searchQuery.toLowerCase());
        const itemTags = [...(item.custom_tags || []), ...(item.date_tags || [])];
        const matchesTags = filterState.selectedTags.length === 0 ||
          filterState.selectedTags.every(tag => itemTags.includes(tag));
        return matchesSearch && matchesTags && filterState.typeVisibility.notes;
      }),
      cards: filterState.selectedItems.cards.filter(item => {
        const matchesSearch = !filterState.searchQuery || 
          item.title.toLowerCase().includes(filterState.searchQuery.toLowerCase());
        const itemTags = [
          ...(item.custom_tags || item.card_data?.custom_tags || []),
          ...(item.date_tags || item.card_data?.date_tags || [])
        ];
        const matchesTags = filterState.selectedTags.length === 0 ||
          filterState.selectedTags.every(tag => itemTags.includes(tag));
        return matchesSearch && matchesTags && filterState.typeVisibility.careerCards;
      })
    };
  }, [filterState]);

  const handleSubmit = () => {
    console.log('Submit clicked, filterState:', filterState);
    // Pass the selected items directly from filterState
    const selectedNotes = filterState.selectedItems.notes;
    const selectedCards = filterState.selectedItems.cards;
    
    console.log('Submitting with selected items:', { selectedNotes, selectedCards });
    onFilterChange(selectedNotes, selectedCards);
    onSubmit();
  };

  const modalFooter = (
    <div className="flex justify-end gap-2 p-4 border-t border-gray-200 dark:border-gray-700">
      <button
        onClick={onClose}
        className={`px-4 py-2 rounded-md ${
          darkMode 
            ? 'bg-gray-700 hover:bg-gray-600 text-white' 
            : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
        }`}
      >
        Cancel
      </button>
      <button
        onClick={handleSubmit}
        className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md"
      >
        {submitButtonText}
      </button>
    </div>
  );

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${
            darkMode ? 'bg-black/75' : 'bg-gray-600/75'
          }`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className={`w-full max-w-4xl rounded-lg shadow-xl ${
              darkMode ? 'bg-gray-800' : 'bg-white'
            }`}
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
          >
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {title}
              </h2>
              <button
                onClick={onClose}
                className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                aria-label="Close filter modal"
                title="Close filter modal"
              >
                <IoCloseOutline className="w-6 h-6 text-gray-500 dark:text-gray-400" />
              </button>
            </div>

            <ErrorBoundary fallback={<div>Error loading filter modal</div>}>
              {isLoading ? (
                <LoadingSpinner />
              ) : (
                <>
                  <div className="p-4">
                    <div className="flex-1 overflow-y-auto">
                      <div className="mb-4">
                        <SearchBar
                          searchQuery={filterState.searchQuery}
                          onSearchChange={handleSearchChange}
                        />
                      </div>
                      <div className="mb-4">
                        <TagsDropdown
                          tags={allTags}
                          selectedTags={filterState.selectedTags}
                          onTagsChange={handleTagsChange}
                          typeVisibility={filterState.typeVisibility}
                          onTypeVisibilityChange={handleTypeVisibilityChange}
                        />
                      </div>
                    </div>
                    <FilterPanels
                      availableItems={filteredAvailableItems}
                      selectedItems={filteredSelectedItems}
                      onItemSelect={handleItemSelect}
                      onItemDeselect={handleItemDeselect}
                      onMoveAllRight={handleMoveAllRight}
                      onMoveAllLeft={handleMoveAllLeft}
                      searchQuery={filterState.searchQuery}
                      selectedTags={filterState.selectedTags}
                      getItemKey={getItemKey}
                    />
                    {children}
                  </div>
                  {modalFooter}
                </>
              )}
            </ErrorBoundary>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PersistentFilterModal; 